import React, { useMemo, useState } from 'react';
import { getAdminTheme } from './adminTheme';

export type AdminPage =
  | 'Dashboard'
  | 'Stock History'
  | 'Alerts'
  | 'Notifications'
  | 'Watchlists'
  | 'Portfolios'
  | 'Tasks'
  | 'Research Desk'
  | 'Research Insights'
  | 'Research Charts'
  | 'Valuation'
  | 'Users'
  | 'Setting';

export type AdminSection = 'Dashboard' | 'Users' | 'Protfolio' | 'Setting';

const getPageLabel = (page: AdminPage): string => {
  switch (page) {
    case 'Tasks':
      return 'Scheduled Tasks';
    default:
      return page;
  }
};

type SectionConfig = {
  id: AdminSection;
  title: string;
  pages: AdminPage[];
};

const SECTIONS: SectionConfig[] = [
  { id: 'Dashboard', title: 'Dashboard', pages: ['Dashboard'] },
  { id: 'Users', title: 'Users', pages: ['Users'] },
  {
    id: 'Protfolio',
    title: 'Protfolio',
    pages: [
      'Stock History',
      'Alerts',
      'Notifications',
      'Watchlists',
      'Portfolios',
      'Research Desk',
      'Research Insights',
      'Research Charts',
        'Valuation',
    ],
  },
  { id: 'Setting', title: 'Setting', pages: ['Setting', 'Tasks'] },
];

interface AdminSidebarProps {
  isDarkMode: boolean;
  expanded: Record<AdminSection, boolean>;
  activePage: AdminPage;
  onToggleSection: (section: AdminSection) => void;
  onSelectPage: (page: AdminPage) => void;
  side?: 'left' | 'right';
  searchQuery?: string;
  onSearchQueryChange?: (value: string) => void;
  brandLogoSrc?: string;
  brandAlt?: string;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  isDarkMode,
  expanded,
  activePage,
  onToggleSection,
  onSelectPage,
  side = 'right',
  searchQuery = '',
  onSearchQueryChange,
  brandLogoSrc = '/images/company-logo.svg',
  brandAlt = 'Company Logo',
}) => {
  const theme = getAdminTheme(isDarkMode);

  const colors = {
    bg: isDarkMode ? '#111827' : theme.cardBg,
    border: theme.border,
    text: theme.text,
    subText: isDarkMode ? '#9CA3AF' : theme.subText,
    hover: theme.hoverBg,
    activeBg: isDarkMode ? theme.border : theme.hoverBg,
    activeText: theme.text,
    inputBg: theme.inputBg,
  };

  const [logoFailed, setLogoFailed] = useState(false);

  const toIdPart = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const q = searchQuery.trim().toLowerCase();
  const isFiltering = q.length > 0;
  const pagesLower = useMemo(() => {
    const map = new Map<AdminPage, string>();
    SECTIONS.forEach((s) =>
      s.pages.forEach((p) => {
        const label = getPageLabel(p);
        map.set(p, `${p.toLowerCase()}|${label.toLowerCase()}`);
      })
    );
    return map;
  }, []);
  const filteredSections = useMemo(() => {
    if (!isFiltering) return SECTIONS;
    return SECTIONS.map((s) => ({
      ...s,
      pages: s.pages.filter((p) => (pagesLower.get(p) ?? '').includes(q)),
    })).filter((s) => s.pages.length > 0);
  }, [isFiltering, pagesLower, q]);

  const borderStyle: React.CSSProperties =
    side === 'left'
      ? { borderRight: `1px solid ${colors.border}` }
      : { borderLeft: `1px solid ${colors.border}` };

  const sectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    fontSize: 13,
    fontWeight: 800,
    color: colors.text,
    cursor: 'pointer',
    userSelect: 'none',
  };

  const itemStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'block',
    padding: '9px 12px',
    fontSize: 13,
    fontWeight: isActive ? 800 : 600,
    color: isActive ? colors.activeText : colors.text,
    backgroundColor: isActive ? colors.activeBg : 'transparent',
    borderRadius: theme.radius,
    cursor: 'pointer',
    margin: '2px 8px',
  });

  return (
    <aside
      id="admin-sidebar"
      style={{
        width: 260,
        backgroundColor: colors.bg,
        ...borderStyle,
        height: '100vh',
        boxSizing: 'border-box',
        position: 'sticky',
        top: 0,
        overflowY: 'auto',
      }}
    >
      <div
        id="admin-sidebar-brand"
        style={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          padding: '0 14px',
          borderBottom: `1px solid ${colors.border}`,
          boxSizing: 'border-box',
          gap: 10,
        }}
      >
        {!logoFailed ? (
          <img
            id="admin-sidebar-logo"
            src={brandLogoSrc}
            alt={brandAlt}
            onError={() => setLogoFailed(true)}
            style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 6, backgroundColor: theme.hoverBg }}
          />
        ) : (
          <div
            id="admin-sidebar-logo-fallback"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              backgroundColor: theme.hoverBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.text,
              fontWeight: 900,
              fontSize: 14,
            }}
          >
            A
          </div>
        )}
        <div id="admin-sidebar-brand-title" style={{ fontWeight: 900, fontSize: 16, letterSpacing: 0.2, color: colors.text }}>
          Admin
        </div>
      </div>

      <div style={{ padding: 12, borderBottom: `1px solid ${colors.border}`, boxSizing: 'border-box' }}>
        <input
          id="admin-sidebar-search"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange?.(e.target.value)}
          placeholder="Search"
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            const first = filteredSections[0]?.pages[0];
            if (first) onSelectPage(first);
          }}
          style={{
            width: '100%',
            height: 34,
            borderRadius: theme.radius,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.inputBg,
            padding: '0 10px',
            color: colors.text,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ padding: '12px 12px 6px', fontSize: 12, fontWeight: 800, color: colors.subText, letterSpacing: 0.3 }}>
        DASHBOARD
      </div>

      {(isFiltering ? filteredSections : SECTIONS).map((section) => {
        const isOpen = isFiltering ? true : Boolean(expanded[section.id]);
        return (
          <div key={section.id} style={{ paddingBottom: 8 }}>
            <div
              style={sectionHeaderStyle}
              id={`admin-sidebar-section-${toIdPart(section.id)}`}
              onClick={() => onToggleSection(section.id)}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.backgroundColor = colors.hover;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
              }}
            >
              <span>{section.title}</span>
              <span style={{ color: colors.subText, fontSize: 14 }}>{isOpen ? '▾' : '▸'}</span>
            </div>

            {isOpen ? (
              <div style={{ paddingBottom: 6 }}>
                {section.pages.map((p) => {
                  const isActive = p === activePage;
                  return (
                    <div
                      key={p}
                      id={`admin-sidebar-page-${toIdPart(p)}`}
                      style={itemStyle(isActive)}
                      onClick={() => onSelectPage(p)}
                      onMouseEnter={(e) => {
                        if (!isActive) (e.currentTarget as HTMLDivElement).style.backgroundColor = colors.hover;
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            backgroundColor: isActive ? '#60A5FA' : '#6B7280',
                            display: 'inline-block',
                          }}
                        />
                        {getPageLabel(p)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </aside>
  );
};

export default AdminSidebar;
